import { useCallback, useMemo, useState } from 'react';

import type { Activity as ActivityType } from '@/repositories';
import { BIG_WIN_RULES, getActivityId, HUGE_WIN_STORAGE_KEY, isBigWinActivity } from '@/utils/activity';

interface UseHugeWinHighlightsOptions {
	activities?: ActivityType[];
	topActivities?: ActivityType[];
	convertAmount: (amount: number, fromCoinType?: string) => number;
	storageKey?: string;
	majorProfitThreshold?: number;
	minorProfitThreshold?: number;
	minorMultiplierThreshold?: number;
	maxPinnedEntries?: number;
	gameFilter?: string;
}

interface HighlightedActivity {
	activity: ActivityType;
	isPinned: boolean;
}

const DEFAULT_MAJOR_PROFIT_THRESHOLD = BIG_WIN_RULES.majorProfitThreshold;
const DEFAULT_MINOR_PROFIT_THRESHOLD = BIG_WIN_RULES.minorProfitThreshold;
const DEFAULT_MINOR_MULTIPLIER_THRESHOLD = BIG_WIN_RULES.minorMultiplierThreshold;

const loadDismissedIds = (storageKey: string): Set<string> => {
	if (typeof window === 'undefined') {
		return new Set();
	}

	try {
		const stored = window.localStorage.getItem(storageKey);
		if (!stored) {
			return new Set();
		}

		const parsed: unknown = JSON.parse(stored);
		if (!Array.isArray(parsed)) {
			return new Set();
		}

		const validIds = parsed.filter((value): value is string => typeof value === 'string' && value.length > 0);
		return new Set(validIds);
	} catch {
		return new Set();
	}
};

export const useHugeWinHighlights = ({
	activities,
	topActivities,
	convertAmount,
	storageKey = HUGE_WIN_STORAGE_KEY,
	majorProfitThreshold = DEFAULT_MAJOR_PROFIT_THRESHOLD,
	minorProfitThreshold = DEFAULT_MINOR_PROFIT_THRESHOLD,
	minorMultiplierThreshold = DEFAULT_MINOR_MULTIPLIER_THRESHOLD,
	maxPinnedEntries = 2,
	gameFilter,
}: UseHugeWinHighlightsOptions) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadDismissedIds(storageKey));

	const matchesGame = useCallback(
		(activity: ActivityType) => {
			if (!gameFilter) return true;
			return activity.from?.toLowerCase?.() === gameFilter.toLowerCase();
		},
		[gameFilter],
	);

	const isBigWin = useCallback(
		(activity: ActivityType) =>
			isBigWinActivity(activity, convertAmount, {
				majorProfitThreshold,
				minorProfitThreshold,
				minorMultiplierThreshold,
			}) && matchesGame(activity),
		[convertAmount, majorProfitThreshold, minorProfitThreshold, minorMultiplierThreshold, matchesGame],
	);

	const highlightedIds = useMemo(() => {
		if (!activities?.length) return new Set<string>();

		const ids = activities
			.filter(isBigWin)
			.map((activity) => getActivityId(activity))
			.filter((id): id is string => Boolean(id));

		return new Set(ids);
	}, [activities, isBigWin]);

	const pinnedHugeWins = useMemo(() => {
		if (!topActivities?.length) return [];

		const seen = new Set<string>();
		const result: ActivityType[] = [];

		for (const activity of topActivities) {
			if (!isBigWin(activity)) continue;
			const activityId = getActivityId(activity);
			if (!activityId || seen.has(activityId) || dismissedIds.has(activityId)) continue;
			seen.add(activityId);
			result.push(activity);
			if (result.length >= maxPinnedEntries) break;
		}

		return result;
	}, [topActivities, dismissedIds, isBigWin, maxPinnedEntries]);

	const pinnedHugeWinIds = useMemo(() => {
		const ids = new Set<string>();
		for (const activity of pinnedHugeWins) {
			const activityId = getActivityId(activity);
			if (activityId) {
				ids.add(activityId);
			}
		}
		return ids;
	}, [pinnedHugeWins]);

	const orderedActivities = useMemo(() => {
		if (!activities?.length) return [];
		return activities.filter((activity) => {
			const activityId = getActivityId(activity);
			if (!activityId) return true;
			return !pinnedHugeWinIds.has(activityId);
		});
	}, [activities, pinnedHugeWinIds]);

	const combinedActivities = useMemo<HighlightedActivity[]>(() => {
		const items: HighlightedActivity[] = [];
		for (const activity of pinnedHugeWins) {
			items.push({ activity, isPinned: true });
		}
		for (const activity of orderedActivities) {
			items.push({ activity, isPinned: false });
		}
		return items;
	}, [pinnedHugeWins, orderedActivities]);

	const dismissHugeWin = useCallback(
		(activityId: string) => {
			if (!activityId) return;
			setDismissedIds((prev) => {
				if (prev.has(activityId)) return prev;
				const next = new Set(prev);
				next.add(activityId);
				if (typeof window !== 'undefined') {
					window.localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
				}
				return next;
			});
		},
		[storageKey],
	);

	return {
		highlightedIds,
		pinnedHugeWins,
		combinedActivities,
		dismissHugeWin,
		isBigWin,
	};
};
